// Състояние (04 §9, 05 §7) — is the machine actually running?
//
// Every scheduled job upserts a `job_health` row on success. The panel's real job is to make
// ABSENCE visible: a job that stopped running writes nothing, so a screen that only listed
// successes would go quietly blank in exactly the situation it exists for. Staleness is therefore
// computed against each job's own expected cadence and shown in words.

import { useJobHealth, useUsageToday } from './useAdmin.js';

/** How long each job may go quiet before "no news" stops being good news. */
const EXPECTED_HOURS: Record<string, number> = {
  pipeline: 26,
  retention: 26,
  audit_history_prune: 26,
  pii_review: 24 * 8,
  charge_generation: 26,
  registry_refresh: 24 * 40, // built locally and published; monthly is the contract
};

const DEFAULT_HOURS = 26;

function age(iso: string | null): { text: string; hours: number } {
  if (iso === null) return { text: 'никога', hours: Infinity };
  const hours = (Date.now() - new Date(iso).getTime()) / 3600_000;
  if (hours < 1) return { text: 'преди по-малко от час', hours };
  if (hours < 48) return { text: `преди ${Math.round(hours)} ч.`, hours };
  return { text: `преди ${Math.round(hours / 24)} дни`, hours };
}

export function HealthPanel() {
  const { data: jobs = [], isLoading } = useJobHealth();
  const { data: usage = [] } = useUsageToday();

  return (
    <div className="adm__health">
      <section className="adm__health-card">
        <div className="adm__section-header">
          <h3 className="adm__h">Фонови cron задачи</h3>
          <span className="adm__sub">Автоматични системни процеси и проверка за изоставане</span>
        </div>

        {isLoading ? <p className="adm__msg">Зареждане на състояние…</p> : null}
        {!isLoading && jobs.length === 0 ? (
          <p className="adm__msg">
            Още никоя задача не е отчела успех. Това е нормално преди първото планирано нощно
            пускане.
          </p>
        ) : null}

        <ul className="adm__jobs">
          {jobs.map((job) => {
            const last = age(job.last_success_at);
            const limit = EXPECTED_HOURS[job.job_name] ?? DEFAULT_HOURS;
            const stale = last.hours > limit;
            return (
              <li key={job.job_name} className={`adm__job ${stale ? 'adm__job--stale' : ''}`}>
                <div className="adm__job-info">
                  <span
                    className={`adm__job-dot ${stale ? 'adm__job-dot--stale' : 'adm__job-dot--ok'}`}
                  />
                  <span className="adm__jobname">{job.job_name}</span>
                </div>
                <div className="adm__job-meta">
                  <span className="adm__jobwhen">Последно: {last.text}</span>
                  {stale ? <span className="adm__jobflag">закъсняла задача</span> : null}
                  {job.last_error ? <span className="adm__joberr">{job.last_error}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="adm__health-card">
        <div className="adm__section-header">
          <h3 className="adm__h">Потребление на API ресурси (24 часа)</h3>
          <span className="adm__sub">Квоти и изразходвани заявки</span>
        </div>

        {usage.length === 0 ? (
          <p className="adm__msg">Няма отчетено API потребление за днес.</p>
        ) : (
          <ul className="adm__usage">
            {usage.map((row) => (
              <li key={row.api_name} className="adm__usage-item">
                <div className="adm__usage-main">
                  <span className="adm__usage-name">{row.api_name}</span>
                  <strong className="adm__usage-units">{row.units} заявки</strong>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="adm__note">
          Броенето е по часовия прозорец на съответния доставчик (Google изчислява по тихоокеанско
          време, PST).
        </p>
      </section>
    </div>
  );
}
